import * as Headless from '@headlessui/react';
import { clsx } from 'clsx';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export function InputGroup({ className, children }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot="control"
      className={twMerge(
        clsx(
          'tw:relative tw:isolate tw:block',
          'tw:has-[>[data-slot=icon]:first-child]:[&_input]:pl-10 tw:has-[>[data-slot=icon]:last-child]:[&_input]:pr-10 tw:sm:has-[>[data-slot=icon]:first-child]:[&_input]:pl-8 tw:sm:has-[>[data-slot=icon]:last-child]:[&_input]:pr-8',
          'tw:[&>[data-slot=icon]]:pointer-events-none tw:[&>[data-slot=icon]]:absolute tw:[&>[data-slot=icon]]:top-3 tw:[&>[data-slot=icon]]:z-10 tw:[&>[data-slot=icon]]:size-5 tw:sm:[&>[data-slot=icon]]:top-2.5 tw:sm:[&>[data-slot=icon]]:size-4',
          'tw:[&>[data-slot=icon]:first-child]:left-3 tw:sm:[&>[data-slot=icon]:first-child]:left-2.5 tw:[&>[data-slot=icon]:last-child]:right-3 tw:sm:[&>[data-slot=icon]:last-child]:right-2.5',
          'tw:[&>[data-slot=icon]]:text-zinc-500 tw:dark:[&>[data-slot=icon]]:text-zinc-400',
          className
        )
      )}
    >
      {children}
    </span>
  );
}

const dateTypes = ['date', 'datetime-local', 'month', 'time', 'week'];
type DateType = (typeof dateTypes)[number];

export const Input = forwardRef(function Input(
  {
    className,
    small,
    ...props
  }: {
    className?: string;
    small?: boolean;
    type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | DateType;
  } & Omit<Headless.InputProps, 'as' | 'className'>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  return (
    <span
      data-slot="control"
      className={twMerge(
        clsx([
          // Basic layout
          'tw:relative tw:block tw:w-full',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'tw:before:absolute tw:before:inset-px tw:before:rounded-[calc(theme(borderRadius.lg)-1px)] tw:before:bg-white tw:before:shadow',
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          'tw:dark:before:hidden',
          // Focus ring
          // 'tw:after:pointer-events-none tw:after:absolute tw:after:inset-0 tw:after:rounded-lg tw:after:ring-inset tw:after:ring-transparent tw:sm:after:focus-within:ring-2 tw:sm:after:focus-within:ring-brand-green-0',
          // Disabled state
          'tw:has-[[data-disabled]]:opacity-50 tw:before:has-[[data-disabled]]:bg-zinc-950/5 tw:before:has-[[data-disabled]]:shadow-none',
          // Invalid state
          'tw:before:has-[[data-invalid]]:shadow-red-500/10',
          className,
        ])
      )}
    >
      <Headless.Input
        ref={ref}
        {...props}
        className={clsx([
          // Date classes
          props.type &&
            dateTypes.includes(props.type) && [
              'tw:[&::-webkit-datetime-edit-fields-wrapper]:p-0',
              'tw:[&::-webkit-date-and-time-value]:min-h-[1.5em]',
              'tw:[&::-webkit-datetime-edit]:inline-flex',
              'tw:[&::-webkit-datetime-edit]:p-0',
              'tw:[&::-webkit-datetime-edit-year-field]:p-0',
              'tw:[&::-webkit-datetime-edit-month-field]:p-0',
              'tw:[&::-webkit-datetime-edit-day-field]:p-0',
              'tw:[&::-webkit-datetime-edit-hour-field]:p-0',
              'tw:[&::-webkit-datetime-edit-minute-field]:p-0',
              'tw:[&::-webkit-datetime-edit-second-field]:p-0',
              'tw:[&::-webkit-datetime-edit-millisecond-field]:p-0',
              'tw:[&::-webkit-datetime-edit-meridiem-field]:p-0',
            ],
          // Basic layout
          small
            ? 'tw:relative tw:block tw:w-full tw:appearance-none tw:rounded-lg tw:px-[calc(theme(spacing[2])-1px)] tw:py-[calc(theme(spacing[0.5])-1px)] tw:sm:px-[calc(theme(spacing[1.5])-1px)] tw:sm:py-[1px]'
            : 'tw:relative tw:block tw:w-full tw:appearance-none tw:rounded-lg tw:px-[calc(theme(spacing[3.5])-1px)] tw:py-[calc(theme(spacing[2.5])-1px)] tw:sm:px-[calc(theme(spacing[3])-1px)] tw:sm:py-[calc(theme(spacing[1.5])-1px)]',
          // Typography
          small
            ? 'tw:text-xs/6 tw:text-zinc-950 tw:placeholder:text-zinc-500 tw:placeholder:text-sm tw:sm:text-[8px]/6 tw:sm:placeholder:text-[12px] tw:dark:text-white tw:!py-0'
            : 'tw:text-base/6 tw:text-zinc-950 tw:placeholder:text-zinc-500 tw:sm:text-sm/6 tw:dark:text-white',
          // Border
          'tw:border tw:border-zinc-950/10 tw:data-[hover]:border-zinc-950/20 tw:dark:border-white/10 tw:dark:data-[hover]:border-white/20',
          // Background color
          'tw:bg-transparent tw:dark:bg-white/5',
          // default focus styles
          'tw:focus:outline-none tw:focus:appearance-none tw:focus-within:inset-0 tw:focus-within:ring-brand-green-1 tw:focus-within:ring-2',
          // Invalid state
          'tw:data-[invalid]:border-red-500 tw:data-[invalid]:data-[hover]:border-red-500 tw:data-[invalid]:dark:border-red-500 tw:data-[invalid]:data-[hover]:dark:border-red-500',
          // Disabled state
          'tw:data-[disabled]:border-zinc-950/20 tw:dark:data-[hover]:data-[disabled]:border-white/15 tw:data-[disabled]:dark:border-white/15 tw:data-[disabled]:dark:bg-white/[2.5%]',
          // System icons
          'tw:dark:[color-scheme:dark]',
        ])}
      />
    </span>
  );
});
